// atlas/apps/web/src/components/EditUrlsModal.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pbAuthedFetch } from '../authHeaders';

interface EditUrlsModalProps {
  onClose: () => void;
}

type Stage = { name: string; state: 'todo' | 'run' | 'done' | 'fail'; out?: string };

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const POLL_MS = 3000;
const POLL_TIMEOUT_MS = 45000;

export default function EditUrlsModal({ onClose }: EditUrlsModalProps) {
  const [hostnames, setHostnames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newHostname, setNewHostname] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stages, setStages] = useState<Stage[] | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  const loadHostnames = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pbAuthedFetch('/api/domain-health/urls');
      const data = await res.json();
      setHostnames(data.hostnames || []);
    } catch {
      setError('Could not load monitored URLs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHostnames(); }, [loadHostnames]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const node = modalRef.current;
    const first = node?.querySelector(FOCUSABLE) as HTMLElement | null;
    (first || node)?.focus();
    return () => { (previouslyFocused.current as HTMLElement | null)?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardedClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!busy) onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = modalRef.current;
      if (!node) return;
      const els = Array.from(node.querySelectorAll(FOCUSABLE)) as HTMLElement[];
      if (!els.length) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  async function waitForProbeData(hostname: string) {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      try {
        const res = await pbAuthedFetch('/api/domain-health');
        const data = await res.json();
        const found = (data.domains || []).find((d: { domain: string }) => d.domain === hostname);
        if (found) return true;
      } catch {
        // keep polling — a transient failure here shouldn't abort the wait
      }
    }
    return false;
  }

  async function handleAdd() {
    const hostname = newHostname.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase();
    if (!HOSTNAME_RE.test(hostname)) {
      setError('Enter a valid hostname, e.g. status.example.com');
      return;
    }
    setError(null);
    setBusy(true);
    setStages([
      { name: 'Updating configuration', state: 'run' },
      { name: 'Reloading Prometheus', state: 'todo' },
      { name: 'Waiting for first probe', state: 'todo' },
    ]);
    try {
      const res = await pbAuthedFetch('/api/domain-health/urls', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ hostname }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setStages((s) => s && [
        { ...s[0], state: 'done' },
        { ...s[1], state: 'done' },
        { ...s[2], state: 'run' },
      ]);
      const found = await waitForProbeData(hostname);
      setStages((s) => s && [s[0], s[1], { ...s[2], state: found ? 'done' : 'fail', out: found ? undefined : 'Still initializing — refresh shortly' }]);
      setNewHostname('');
      await loadHostnames();
    } catch (err) {
      setStages((s) => s && s.map((stage) => (stage.state === 'run' ? { ...stage, state: 'fail', out: (err as Error).message } : stage)));
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(hostname: string) {
    setError(null);
    setBusy(true);
    setStages([
      { name: 'Updating configuration', state: 'run' },
      { name: 'Reloading Prometheus', state: 'todo' },
      { name: 'Done', state: 'todo' },
    ]);
    try {
      const res = await pbAuthedFetch(`/api/domain-health/urls/${encodeURIComponent(hostname)}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setStages((s) => s && s.map((stage) => ({ ...stage, state: 'done' })));
      await loadHostnames();
    } catch (err) {
      setStages((s) => s && s.map((stage) => (stage.state === 'run' ? { ...stage, state: 'fail', out: (err as Error).message } : stage)));
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="ingest-modal-overlay" onClick={guardedClose}>
      <div className="ingest-modal" ref={modalRef} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Edit monitored URLs">
        <div className="ingest-modal-head">
          <h2>Monitored URLs</h2>
          <button className="ingest-modal-x" onClick={guardedClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <div className="ingest-modal-body">
          {error && <p className="notice notice--error">{error}</p>}

          {stages ? (
            <div className="ingest-stages">
              {stages.map((s) => (
                <div key={s.name} className={`ingest-stage ${s.state}`}>
                  <span className="ingest-stage-dot">{s.state === 'done' ? '✓' : s.state === 'fail' ? '!' : ''}</span>
                  <span className="ingest-stage-name">{s.name}</span>
                  <span className="ingest-stage-out">{s.out}</span>
                </div>
              ))}
              {!busy && (
                <button className="btn btn--sm" onClick={() => setStages(null)} style={{ marginTop: 'var(--space-3)' }}>Back to list</button>
              )}
            </div>
          ) : (
            <>
              {loading ? (
                <p className="page-loading">Loading…</p>
              ) : (
                <ul className="edit-urls-list">
                  {hostnames.map((h) => (
                    <li key={h}>
                      <span>{h}</span>
                      <button className="ibtn" title={`Remove ${h}`} aria-label={`Remove ${h}`} onClick={() => handleRemove(h)} disabled={busy}>✕</button>
                    </li>
                  ))}
                  {hostnames.length === 0 && <li className="edit-urls-empty">No monitored URLs yet.</li>}
                </ul>
              )}
              <div className="edit-urls-add">
                <input
                  type="text"
                  placeholder="status.example.com"
                  value={newHostname}
                  onChange={(e) => setNewHostname(e.target.value)}
                  disabled={busy}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                />
                <button className="btn btn--primary btn--sm" onClick={handleAdd} disabled={busy || !newHostname.trim()}>+ Add</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
